import { Experiment, ExperimentClient, ExperimentUser } from '@amplitude/experiment-js-client';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import envService from './env.service';

// Feature flags that have a boolean value
export enum FeatureFlags {
  Test = 'test',
  IsSkipAccountCreationEnabled = 'skip-email-capture-pre-checkout-grow-131_v2',
  IsRiskTrendTabEnabled = 'risk-trend-tab-enabled',
  IsRiskAssessmentUpgradeEnabled = 'risk-assessment-upgrade',
  IsSinglePaymentEnabled = 'single-payment-enabled',
}

// Feature flags that have multiple variants as a string
export enum VariantFlags {
  PPRAVersion = 'ppra-version',
  IsPpraMembershipInterstitialEnabled = 'ppra-membership-interstitial-enabled',
  RemoveNameFromEmailCapture = 'remove-name-from-email-capture-grow-671',
  ShowHeroVideoInterstitial = 'show-hero-video-interstitial-grow-670',
  QuizletVersion = 'grow-720-quizlet-version'
}

const defaultFeatureFlags: Record<VariantFlags, string> = {
  [VariantFlags.PPRAVersion]: 'email_first',
  [VariantFlags.IsPpraMembershipInterstitialEnabled]: 'control',
  [VariantFlags.RemoveNameFromEmailCapture]: 'control',
  [VariantFlags.ShowHeroVideoInterstitial]: 'control',
  [VariantFlags.QuizletVersion]: 'v1',
};

export interface FeatureFlagContextValue {
  isEnabled: (name: FeatureFlags) => boolean;
  getVariant: (name: VariantFlags) => string;
  isLoading: boolean;
  setUser: (user?: ExperimentUser) => Promise<void>;
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

export const FeatureFlagProvider: FC<PropsWithChildren> = ({ children }) => {
  const [instance, setInstance] = useState<ExperimentClient>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeFeatureFlags = async () => {
      try {
        setIsLoading(true);

        // Initialize Experiment client with Amplitude Analytics integration
        // This ensures the device ID is properly shared between Analytics and Experiment
        const experimentInstance = Experiment.initializeWithAmplitudeAnalytics(
          envService.getEnv().VITE_AMPLITUDE_API_KEY
        );

        await experimentInstance.fetch();

        setInstance(experimentInstance);

      } catch (error) {
        console.error('Failed to initialize feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFeatureFlags();
  }, []);

  const isEnabled = (name: FeatureFlags): boolean => {
    if (!instance || isLoading) return false;
    const variant = instance.variant(name);
    return variant.value === 'on';
  };

  const getVariant = (name: VariantFlags): string => {
    if (!instance || isLoading) return defaultFeatureFlags[name];
    const variant = instance.variant(name);
    return variant.value ?? defaultFeatureFlags[name];
  };

  const setUser = async (user?: ExperimentUser): Promise<void> => {
    if (instance && user) {
      // When using initializeWithAmplitudeAnalytics, the device ID is automatically
      // synchronized between Analytics and Experiment SDKs
      instance.setUser(user);
      await instance.fetch(user);
    }
  };

  const contextValue: FeatureFlagContextValue = {
    isEnabled,
    getVariant,
    isLoading,
    setUser,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

export default FeatureFlagProvider;
